import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Building2,
  Download,
  Search,
  Filter,
  RefreshCw,
  FileSpreadsheet,
  Mail,
  Phone,
  MapPin,
  User,
  Calendar,
  Users
} from 'lucide-react';
import { adminApi } from '../services/authApi';
import { useToast } from '../hooks/use-toast';
import LoadingSpinner from './LoadingSpinner';

const PracticeInfo = () => {
  const [practices, setPractices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [exporting, setExporting] = useState(false);
  const { toast } = useToast();

  const loadPractices = async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const token = localStorage.getItem('adminToken');

      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter) params.append('status_filter', statusFilter);

      const response = await fetch(
        `${backendUrl}/api/admin/practice-info?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to load practices');
      }

      const data = await response.json();
      setPractices(data.practices || []);
    } catch (error) {
      console.error('Failed to load practices:', error);
      toast({
        title: "Loading Failed",
        description: "Could not load practice information. Please try again.",
        variant: "destructive"
      });
    } finally{
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadPractices();
  }, [statusFilter]);

  const handleSearch = () => {
    loadPractices();
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const token = localStorage.getItem('adminToken');

      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter) params.append('status_filter', statusFilter);

      const endpoint = format === 'csv' 
        ? '/api/admin/practice-info/export-csv'
        : '/api/admin/practice-info/export-excel';

      const response = await fetch(
        `${backendUrl}${endpoint}?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Export failed');
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `practice_info_${new Date().toISOString().split('T')[0]}.${format === 'csv' ? 'csv' : 'xlsx'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: "Export Successful",
        description: `Practice information exported as ${format.toUpperCase()}`,
        variant: "default"
      });
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: "Export Failed",
        description: "Could not export practice information. Please try again.",
        variant: "destructive"
      });
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'N/A';
    }
  };

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return <Badge className="bg-green-100 text-green-800 border-green-200">Active</Badge>;
      case 'trial':
        return <Badge className="bg-blue-100 text-blue-800 border-blue-200">Trial</Badge>;
      case 'beta':
        return <Badge className="bg-purple-100 text-purple-800 border-purple-200">Beta</Badge>;
      case 'cancelled':
      case 'inactive':
        return <Badge variant="outline" className="bg-gray-100">Inactive</Badge>;
      default:
        return <Badge variant="secondary">{status || 'Unknown'}</Badge>;
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Building2 className="h-5 w-5 mr-2 text-blue-600" />
            Practice Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner />
            <span className="ml-3 text-gray-600">Loading practice information...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="max-w-7xl mx-auto">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center">
            <Building2 className="h-5 w-5 mr-2 text-blue-600" />
            Practice Information ({practices.length})
          </CardTitle>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExport('csv')}
              disabled={exporting || practices.length === 0}
              className="flex items-center"
            >
              <Download className="h-4 w-4 mr-1" />
              Export CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExport('excel')}
              disabled={exporting || practices.length === 0}
              className="flex items-center"
            >
              <FileSpreadsheet className="h-4 w-4 mr-1" />
              Export Excel
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => loadPractices(true)}
              disabled={refreshing}
              className="flex items-center"
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {/* Filters */}
        <div className="mb-6 flex flex-col md:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by practice name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              className="pl-10"
            />
          </div>
          <Button onClick={handleSearch} className="bg-blue-600 hover:bg-blue-700">
            <Search className="h-4 w-4 mr-1" />
            Search
          </Button>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="trial">Trial</option>
            <option value="beta">Beta</option>
            <option value="cancelled">Cancelled</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        {/* Practice List */}
        {practices.length === 0 ? (
          <div className="text-center py-12">
            <Building2 className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-xl font-medium text-gray-900 mb-2">No Practices Found</h3>
            <p className="text-gray-600">
              {searchTerm || statusFilter
                ? 'Try adjusting your search or filters'
                : 'No practices have been registered yet.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div className="space-y-4">
              {practices.map((practice, index) => (
                <div
                  key={practice.practice_id || index}
                  className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h4 className="font-semibold text-gray-900 text-lg">
                          {practice.practice_name || 'Unnamed Practice'}
                        </h4>
                        {getStatusBadge(practice.subscription_status)}
                        {practice.is_beta_tester && (
                          <Badge className="bg-purple-100 text-purple-800">Beta Tester</Badge>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                    {/* Contact Person */}
                    {practice.dentist_full_name && (
                      <div className="flex items-center space-x-2 text-gray-700">
                        <User className="h-4 w-4 text-blue-600" />
                        <span className="font-medium">Contact:</span>
                        <span>{practice.dentist_full_name}</span>
                      </div>
                    )}

                    {/* Email */}
                    {practice.contact_email && (
                      <div className="flex items-center space-x-2 text-gray-700">
                        <Mail className="h-4 w-4 text-green-600" />
                        <a
                          href={`mailto:${practice.contact_email}`}
                          className="text-blue-600 hover:text-blue-800 underline"
                        >
                          {practice.contact_email}
                        </a>
                      </div>
                    )}

                    {/* Phone */}
                    {practice.practice_phone && (
                      <div className="flex items-center space-x-2 text-gray-700">
                        <Phone className="h-4 w-4 text-purple-600" />
                        <a
                          href={`tel:${practice.practice_phone}`}
                          className="text-blue-600 hover:text-blue-800 underline"
                        >
                          {practice.practice_phone}
                        </a>
                      </div>
                    )}

                    {/* Address */}
                    {practice.practice_address && (
                      <div className="flex items-start space-x-2 text-gray-700">
                        <MapPin className="h-4 w-4 text-red-600 mt-0.5" />
                        <span>{practice.practice_address}</span>
                      </div>
                    )}

                    {/* Created Date */}
                    <div className="flex items-center space-x-2 text-gray-600">
                      <Calendar className="h-4 w-4" />
                      <span>Joined: {formatDate(practice.created_date)}</span>
                    </div>

                    {/* Stats */}
                    <div className="flex items-center space-x-2 text-gray-600">
                      <Users className="h-4 w-4" />
                      <span>
                        {practice.total_patients} patients, {practice.total_procedures} procedures
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Info Box */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> This data is optimized for email campaigns and marketing purposes. 
            Export to CSV or Excel to use with your email marketing tools.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default PracticeInfo;
